import { Form as FormikForm, Formik } from 'formik';
import React, { useState } from 'react';
import { Image, Stack } from 'react-bootstrap';
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import SFCLogoMD from '../../assets/images/sticky-fingers-logo-stacked-2.png';
import { SimpleButton } from '../../components/buttons/SimpleButton';
import { PasswordField } from '../../components/inputs/PasswordField';
import { useResetPasswordMutation } from '../../services/endpoints/people/user';
import { PageMeta } from '../../components/meta/PageMeta';
import { getErrorMessage } from '../../utils/utils';
import { FadeIn } from '../animations/FadeIn';

type Props = {};

interface FormValues {
  password: string;
  confirm_password: string;
}

const ForgotPassword: React.FC<Props> = () => {
  const navigate: NavigateFunction = useNavigate();
  const { token } = useParams();

  const [resetPassword] = useResetPasswordMutation();

  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  const initialValues: FormValues = {
    password: '',
    confirm_password: ''
  };

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .min(6, 'Password must be at least 6 characters')
      .required('Password is required'),
    confirm_password: Yup.string()
      .oneOf([Yup.ref('password')], 'Passwords must match')
      .required('Confirm Password is required')
  });

  const sendInstructions = (formValue: FormValues) => {
    const { password } = formValue;

    setLoading(true);
    setMessage('');
    if (token) {
      resetPassword({ password, token })
        .unwrap()
        .then((response) => {
          setSuccessMessage(response.message);
        })
        .catch((error) => {
          setMessage(getErrorMessage(error));
        });
    } else {
      setMessage('Invalid or missing token!');
    }
    setLoading(false);
  };

  return (
    <FadeIn className="d-flex flex-column align-content-center flex-wrap">
      <PageMeta title="Reset Password" />
      <div className="pb-4">
        <Image
          fluid
          className="d-block d-sm-none"
          src={SFCLogoMD}
          alt="Sticky Fingers Logo"
          onClick={() => navigate('/')}
        />
        <Image
          className="d-none d-sm-inline-flex"
          src={SFCLogoMD}
          style={{ maxWidth: 343 }}
          alt="Sticky Fingers Logo"
          onClick={() => navigate('/')}
        />
      </div>

      <h2
        className="text-dark fw-bold mt-lg-5 text-md-center mt-4 text-start"
        style={{ fontSize: 32 }}
      >
        Reset password
      </h2>
      <Stack
        className="w-100 mt-3"
        style={{ maxWidth: 480 }}
        gap={1}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={sendInstructions}
        >
          {() => (
            <FormikForm className="text-start">
              <PasswordField name="password" label="Password" />
              <PasswordField name="confirm_password" label="Confirm Password" />

              <SimpleButton
                type="submit"
                className="w-100 mt-3"
                variant="primary"
                disabled={loading}
              >
                {loading && <span className="spinner-border spinner-border-sm"></span>}
                Reset Password
              </SimpleButton>

              {message && (
                <div className="form-group">
                  <div
                    className="alert alert-danger"
                    role="alert"
                  >
                    {message}
                  </div>
                </div>
              )}

              {successMessage && (
                <div className="form-group">
                  <div
                    className="alert alert-success"
                    role="alert"
                  >
                    {successMessage}
                  </div>
                </div>
              )}
            </FormikForm>
          )}
        </Formik>
        <div hidden={successMessage === ''}>
          <div className="d-flex justify-content-center align-items-center my-3">
            <span className="ff-inter fs-small text-secondary">Ready to login?</span>
            <button
              onClick={() => navigate('/users/login')}
              className="btn btn-link fs-small fw-bolder text-uppercase link-primary link-underline-opacity-0 ms-2 p-0"
            >
              Sign In
            </button>
          </div>
        </div>
      </Stack>
    </FadeIn>
  );
};

export default ForgotPassword;
